import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { usePagination } from "../hooks";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputBase,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  Select,
  TablePagination,
  Divider,
  Chip,
  Tooltip,
  Skeleton,
} from "@mui/material";
import AsignarPorUsuarioModal from "../components/Asignaciones/ComponentAsignarPorUsuarioModal.js";
import { SCREEN_IDS } from "../components/FullComponent.js";
import {
  Search as SearchIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  PersonAdd as PersonAddIcon,
} from "@mui/icons-material";
import {
  getFullMoNotNull,
  insertControlDePieza,
  getUserTaller,
  setLog,
  insertDatosTrabajoExterno,
} from "../services/ConnectDataBase";
import { getCurrentDateTime } from "../services/Functions";
import { updateListaPesos } from "../redux/reducers";
import { DEPARTAMENTS, typeConnect } from "../constants/Constants";
import { formatDate, formatNumber } from "../services/Functions";
import { translateText } from "../translations/translationService";
import { BRAND_COLORS, BORDER_COLORS, TEXT_COLORS, BACKGROUND_COLORS, PARTS_MOVEMENTS_COLORS, STATUS_COLORS } from "../style/colors.js";
import { createExternoModal, createExternoBulkModal, detectExternoType } from "../services/utils/ExternoModalFactory.js";
import { notify } from "../services/ui/notify";

export default function PartsMovements() {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const lang = useSelector((state) => state.lang.value);
  const screenName = "partsMovements";
  const currentScreenId = SCREEN_IDS.PARTS_MOVEMENTS;
  const listData = useSelector((state) => state.pesosList.listaPesos);
  const usuario = useSelector((state) => state.user.name);
  const equipo = useSelector((state) => state.user.team);
  const [searchResult, setSearchResult] = useState(null);
  const [usuariosTaller, setUsuariosTaller] = useState([]);
  const [usuarioSeleccionados, setusuarioSeleccionados] = useState({});
  const [openAsignarPorUsuario, setOpenAsignarPorUsuario] = useState(false);
  const [mostrarTodosUsuarios, setMostrarTodosUsuarios] = useState(false);
  
  // Estados para el modal de externos
  const [externoModal, setExternoModal] = useState(null);
  const [isExternoModalOpen, setIsExternoModalOpen] = useState(false);

  // Hook de paginación
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination(0, 25);

  const seleccionarUsuario = (idFila, usuario) => {
    setusuarioSeleccionados((prevUsuarios) => ({
      ...prevUsuarios,
      [idFila]: usuario,
    }));
  };

  const getMo = useCallback(async () => {
    setIsLoading(true);
    try {
      const fullMo = await getFullMoNotNull(
        screenName,
        typeConnect[0],
        currentScreenId
      );

      const moArray = Object.entries(fullMo).map(([po, mo], index) => ({
        key: po,
        po,
        ...mo,
      }));

      setIsLoading(false);
      dispatch(updateListaPesos(moArray));
      getUser();
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  }, [dispatch, screenName, currentScreenId]);

  const getUser = useCallback(async () => {
    try {
      const fullMo = await getUserTaller(
        screenName,
        typeConnect[0],
        currentScreenId
      );

      const listaUsuarios = Object.entries(fullMo).map(([po, mo], index) => ({
        key: po,
        ...mo,
      }));

      const listaUsuariosConNombreDeTeam = listaUsuarios.map((usuario) => ({
        ...usuario,
        cod_team: DEPARTAMENTS[String(usuario.cod_team)] || usuario.cod_team,
      }));

      setUsuariosTaller(listaUsuariosConNombreDeTeam);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    getMo();
  }, [getMo]);

  const handleSearch = (searchValue) => {
    const trimmedRefSearch = searchValue.trim();
    if (trimmedRefSearch === "") {
      setSearchResult(null);
      return;
    }

    const searchTerms = trimmedRefSearch.split(" ").map((term) => term.trim());

    const result = listData.filter((item) => {
      const moModified = item.mo.replace("'", "-");
      return searchTerms.includes(moModified);
    });

    setSearchResult(result);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleAddEntrega = useCallback(
    async (item, dispatchUpdate = true) => {
      try {
        const status = await insertControlDePieza(
          item.po,
          item.mo,
          usuario,
          1,
          getCurrentDateTime(),
          "Entregado.",
          currentScreenId
        );
        if (status) {
          const updatedItem = { ...item, entrega_pieza: usuario };
          if (dispatchUpdate) {
            const updatedListData = listData.map((i) =>
              i.key === item.key ? updatedItem : i
            );
            dispatch(updateListaPesos(updatedListData));
            if (searchResult) {
              setSearchResult(
                searchResult.map((i) => (i.key === item.key ? updatedItem : i))
              );
            }
          }
          return updatedItem;
        }
      } catch (error) {
        console.error(error);
      }
    },
    [usuario, currentScreenId, dispatch, listData, searchResult]
  );

  const handleAddRecibido = useCallback(
    async (item, dispatchUpdate = true) => {
      try {
        const user = item.recibe_pieza || usuario;
        const status = await insertControlDePieza(
          item.po,
          item.mo,
          user,
          1,
          getCurrentDateTime(),
          "Recibido.",
          currentScreenId
        );
        if (status) {
          const updatedItem = { ...item, recibe_pieza: user };
          if (dispatchUpdate) {
            const updatedListData = listData.map((i) =>
              i.key === item.key ? updatedItem : i
            );
            dispatch(updateListaPesos(updatedListData));
            if (searchResult) {
              setSearchResult(
                searchResult.map((i) => (i.key === item.key ? updatedItem : i))
              );
            }
          }
          return updatedItem;
        }
      } catch (error) {
        console.error(error);
      }
    },
    [usuario, currentScreenId, dispatch, listData, searchResult]
  );

  const handleAddEntRec = useCallback(
    async (item, recibeUsuarioParam = null) => {
      const recibeUsuario =
        recibeUsuarioParam || usuarioSeleccionados[item.key] || usuario;

      // Verificar si es un usuario externo
      const externoType = detectExternoType(recibeUsuario);
      
      if (externoType) {
        // Crear y mostrar modal para usuario externo
        try {
          const modalConfig = createExternoModal(
            recibeUsuario, 
            item, 
            () => {
              setIsExternoModalOpen(false);
              setExternoModal(null);
            },
            async (moDataList, userName) => {
              // Función de asignación para el modal individual
              const moData = moDataList[0]; // Modal individual solo tiene una MO
              const updatedItem1 = await handleAddEntrega(moData, false);
              const updatedItem2 = await handleAddRecibido(
                { ...updatedItem1, recibe_pieza: userName },
                false
              );
              const finalUpdatedItem = { ...updatedItem1, ...updatedItem2 };

              // Actualizar el estado con el elemento final
              const updatedListData = listData.map((i) =>
                i.key === finalUpdatedItem.key ? finalUpdatedItem : i
              );
              dispatch(updateListaPesos(updatedListData));

              if (searchResult) {
                setSearchResult(
                  searchResult.map((i) => (i.key === finalUpdatedItem.key ? finalUpdatedItem : i))
                );
              }

              const logData = `Entrega pieza: ${
                finalUpdatedItem.entrega_pieza
              }, recibe: ${finalUpdatedItem.recibe_pieza}, mo: ${
                finalUpdatedItem.mo
              }, fecha y hora: ${new Date().toLocaleString()}`;
              await setLog(SCREEN_IDS.GENERAL, "LOG", "Asignacion", logData);

              // Insertar automáticamente en trabajo externo para Engastador (para que quede registro aunque no se guarde manualmente)
              if (externoType === 'EXTERNO_Engastador') {
                try {
                  const datosTrabajoExterno = [{
                    mo: finalUpdatedItem.mo,
                    referencia: finalUpdatedItem.referencia || "",
                    trabajo: "Engastado",
                    usuario: userName,
                    cantidad_entregada: Math.round(parseFloat(finalUpdatedItem.cantidad || 0)),
                    cantidad_devuelta: 0,
                    fecha_entrega_pieza: { date: getCurrentDateTime() }
                  }];
                  await insertDatosTrabajoExterno(SCREEN_IDS.GENERAL, "insertarDatosTrabajoExterno", datosTrabajoExterno);
                } catch (error) {
                  console.error("Error insertando en trabajo externo:", error);
                }
              }
            }
          );
          
          if (modalConfig) {
            setExternoModal(modalConfig);
            setIsExternoModalOpen(true);
            return; // No continuar con la asignación normal
          }
        } catch (error) {
          console.error('Error creando modal de externo:', error);
        }
      }

      // Proceso normal para usuarios no externos
      const updatedItem1 = await handleAddEntrega(item, false);
      const updatedItem2 = await handleAddRecibido(
        { ...updatedItem1, recibe_pieza: recibeUsuario },
        false
      );
      const finalUpdatedItem = { ...updatedItem1, ...updatedItem2 };

      // Actualizar el estado con el elemento final
      const updatedListData = listData.map((i) =>
        i.key === item.key ? finalUpdatedItem : i
      );
      dispatch(updateListaPesos(updatedListData));

      if (searchResult) {
        setSearchResult(
          searchResult.map((i) => (i.key === item.key ? finalUpdatedItem : i))
        );
      }

      const logData = `Entrega pieza: ${
        finalUpdatedItem.entrega_pieza
      }, recibe: ${finalUpdatedItem.recibe_pieza}, mo: ${
        finalUpdatedItem.mo
      }, fecha y hora: ${new Date().toLocaleString()}`;
      await setLog(SCREEN_IDS.GENERAL, "LOG", "Asignacion", logData);
    },
    [
      handleAddEntrega,
      handleAddRecibido,
      usuarioSeleccionados,
      usuario,
      listData,
      dispatch,
      searchResult,
    ]
  );

  const toggleMostrarTodosUsuarios = () => {
    setMostrarTodosUsuarios(!mostrarTodosUsuarios);
  };

  const handleAssignAll = useCallback(
    async (usuarioAsignado) => {
      if (!usuarioAsignado) {
        notify.warn("Por favor seleccione un usuario.");
        return;
      }
      if (!searchResult || searchResult.length === 0) {
        notify.warn("No hay MOs seleccionadas para asignar.");
        return;
      }
      if (searchResult.length > 30) {
        notify.warn("Solo se pueden asignar hasta 30 MOs al mismo tiempo.");
        return;
      }

      // Verificar si es un usuario externo
      const externoType = detectExternoType(usuarioAsignado);
      
      if (externoType) {
        // Crear y mostrar modal bulk para usuario externo
        try {
          const modalConfig = createExternoBulkModal(
            usuarioAsignado, 
            searchResult, 
            () => {
              setIsExternoModalOpen(false);
              setExternoModal(null);
            },
            async (moDataList, userName) => {
              // Función de asignación para el modal bulk
              const updatedItems = [];
              const datosTrabajoExterno = [];
              
              for (const item of moDataList) {
                const updatedItem1 = await handleAddEntrega(item, false);
                const updatedItem2 = await handleAddRecibido(
                  { ...updatedItem1, recibe_pieza: userName },
                  false
                );
                const finalUpdatedItem = { ...updatedItem1, ...updatedItem2 };
                updatedItems.push(finalUpdatedItem);

                const logData = `Entrega pieza: ${
                  finalUpdatedItem.entrega_pieza
                }, recibe: ${finalUpdatedItem.recibe_pieza}, mo: ${
                  finalUpdatedItem.mo
                }, fecha y hora: ${new Date().toLocaleString()}`;
                await setLog(SCREEN_IDS.GENERAL, "LOG", "Asignacion", logData);

                // Preparar datos para insertar en trabajo externo (solo para Engastador)
                if (externoType === 'EXTERNO_Engastador') {
                  datosTrabajoExterno.push({
                    mo: finalUpdatedItem.mo,
                    referencia: finalUpdatedItem.referencia || "",
                    trabajo: "Engastado",
                    usuario: userName,
                    cantidad_entregada: Math.round(parseFloat(finalUpdatedItem.cantidad || 0)),
                    cantidad_devuelta: 0,
                    fecha_entrega_pieza: { date: getCurrentDateTime() }
                  });
                }
              }

              const updatedListData = listData.map((item) => {
                const updatedItem = updatedItems.find(
                  (updated) => updated.key === item.key
                );
                return updatedItem || item;
              });

              dispatch(updateListaPesos(updatedListData));

              // Actualizar también searchResult si existe
              if (searchResult) {
                const updatedSearchResult = searchResult.map((item) => {
                  const updatedItem = updatedItems.find(
                    (updated) => updated.key === item.key
                  );
                  return updatedItem || item;
                });
                setSearchResult(updatedSearchResult);
              }

              // Insertar automáticamente en trabajo externo para Engastador (bulk)
              if (externoType === 'EXTERNO_Engastador' && datosTrabajoExterno.length > 0) {
                try {
                  await insertDatosTrabajoExterno(SCREEN_IDS.GENERAL, "insertarDatosTrabajoExterno", datosTrabajoExterno);
                } catch (error) {
                  console.error("Error insertando en trabajo externo:", error);
                }
              }
            }
          );
          
          if (modalConfig) {
            setExternoModal(modalConfig);
            setIsExternoModalOpen(true);
            return; // No continuar con la asignación normal
          }
        } catch (error) {
          console.error('Error creando modal bulk de externo:', error);
        }
      }

      // Proceso normal para usuarios no externos
      const updatedItems = [];
      for (const item of searchResult) {
        const updatedItem1 = await handleAddEntrega(item, false);
        const updatedItem2 = await handleAddRecibido(
          { ...updatedItem1, recibe_pieza: usuarioAsignado },
          false
        );
        const finalUpdatedItem = { ...updatedItem1, ...updatedItem2 };
        updatedItems.push(finalUpdatedItem);

        const logData = `Entrega pieza: ${
          finalUpdatedItem.entrega_pieza
        }, recibe: ${finalUpdatedItem.recibe_pieza}, mo: ${
          finalUpdatedItem.mo
        }, fecha y hora: ${new Date().toLocaleString()}`;
        await setLog(SCREEN_IDS.GENERAL, "LOG", "Asignacion", logData);
      }

      const updatedListData = listData.map((item) => {
        const updatedItem = updatedItems.find(
          (updated) => updated.key === item.key
        );
        return updatedItem || item;
      });

      dispatch(updateListaPesos(updatedListData));

      // Actualizar también searchResult si existe
      if (searchResult) {
        const updatedSearchResult = searchResult.map((item) => {
          const updatedItem = updatedItems.find(
            (updated) => updated.key === item.key
          );
          return updatedItem || item;
        });
        setSearchResult(updatedSearchResult);
      }
    },
    [searchResult, handleAddEntrega, handleAddRecibido, listData, dispatch]
  );

  const usuariosFiltrados = mostrarTodosUsuarios
    ? usuariosTaller
    : usuariosTaller.filter(
        (usuario) =>
          usuario.cod_team === equipo ||
          equipo === "IT" ||
          equipo === "Jefe de taller" ||
          equipo === "Dirección" ||
          equipo === "Control de calidad"
      );

  const SearchBar = ({
    handleSearch,
    toggleMostrarTodosUsuarios,
    mostrarTodosUsuarios,
    inputRef,
    usuariosFiltrados,
    handleAssignAll,
    searchResult,
  }) => {
    const [searchValue, setSearchValue] = useState("");
    const [usuarioSeleccionado, setUsuarioSeleccionado] = useState("");

    const handleInputChange = (e) => {
      let newValue = e.target.value.replace(/'/g, "-");

      if (
        newValue.replace(/ /g, "").length % 10 === 0 &&
        !newValue.endsWith(" ")
      ) {
        newValue += " ";
      }

      setSearchValue(newValue);
    };

    return (
      <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1.5,
            padding: 1.5,
            backgroundColor: "white",
            borderRadius: 2,
            boxShadow:
              "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
            border: `1px solid ${PARTS_MOVEMENTS_COLORS.BACKGROUND_INPUT}`,
            marginBottom: 1.5,
          }}>
        <Box sx={{ flex: 1, minWidth: "280px", position: "relative" }}>
          <InputBase
            inputRef={inputRef}
            value={searchValue}
            onChange={handleInputChange}
            sx={{
              width: "100%",
              backgroundColor: PARTS_MOVEMENTS_COLORS.BACKGROUND_CARD,
              color: PARTS_MOVEMENTS_COLORS.TEXT_CARD,
              fontSize: "0.85rem",
              fontWeight: 500,
              padding: "8px 12px 8px 40px",
              borderRadius: 2,
              border: `1.5px solid ${PARTS_MOVEMENTS_COLORS.BORDER_CARD}`,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: PARTS_MOVEMENTS_COLORS.BACKGROUND_INPUT,
                borderColor: PARTS_MOVEMENTS_COLORS.BORDER_BRAND,
                boxShadow: "0 2px 4px rgba(70, 93, 100, 0.1)",
              },
              "&.Mui-focused": {
                backgroundColor: "white",
                borderColor: PARTS_MOVEMENTS_COLORS.BORDER_BRAND,
                boxShadow:
                  "0 0 0 2px rgba(70, 93, 100, 0.1), 0 2px 4px rgba(70, 93, 100, 0.1)",
              },
              "& input": {
                padding: 0,
              },
              "& input::placeholder": {
                color: PARTS_MOVEMENTS_COLORS.TEXT_MUTED,
                opacity: 1,
              },
            }}
            placeholder={translateText("Código de MO", lang)}
          />

          <Box
            sx={{
              position: "absolute",
              left: 16,
              top: "50%",
              transform: "translateY(-50%)",
              color: PARTS_MOVEMENTS_COLORS.TEXT_MUTED,
              pointerEvents: "none",
            }}>
            <SearchIcon sx={{ fontSize: 18 }} />
          </Box>
        </Box>

        <Button
          onClick={() => handleSearch(searchValue)}
          variant='contained'
          size="small"
          startIcon={<SearchIcon />}
          sx={{
            backgroundColor: PARTS_MOVEMENTS_COLORS.BACKGROUND_BUTTON_PRIMARY,
            color: "white",
            borderRadius: 2,
            fontSize: "0.8rem",
            fontWeight: 600,
            padding: "6px 16px",
            textTransform: "none",
            boxShadow: "none",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: PARTS_MOVEMENTS_COLORS.BACKGROUND_BUTTON_SECONDARY,
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(70, 93, 100, 0.2)",
            },
          }}>
          Buscar
        </Button>

        <Box
          sx={{
            display: { xs: "none", lg: "block" },
            width: "1px",
            height: 32,
            backgroundColor: PARTS_MOVEMENTS_COLORS.BORDER_CARD,
          }}
        />

        <Select
          value={usuarioSeleccionado}
          onChange={(e) => setUsuarioSeleccionado(e.target.value)}
          displayEmpty
          variant='outlined'
          size="small"
          startAdornment={
            <Box sx={{ mr: 1, display: "flex", alignItems: "center" }}>
              <PersonAddIcon sx={{ fontSize: 16, color: PARTS_MOVEMENTS_COLORS.TEXT_MUTED }} />
            </Box>
          }
          MenuProps={{
            PaperProps: {
              sx: {
                borderRadius: 2,
                border: `1.5px solid ${PARTS_MOVEMENTS_COLORS.BORDER_BRAND}`,
                boxShadow:
                  "0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)",
                mt: 1,
                maxHeight: 400,
                "& .MuiMenuItem-root": {
                  fontSize: "0.75rem",
                  padding: "8px 12px",
                  color: PARTS_MOVEMENTS_COLORS.TEXT_DARK,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(70, 93, 100, 0.08)",
                    color: PARTS_MOVEMENTS_COLORS.TEXT_BRAND,
                  },
                  "&.Mui-selected": {
                    backgroundColor: "rgba(70, 93, 100, 0.12)",
                    color: PARTS_MOVEMENTS_COLORS.TEXT_BRAND,
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "rgba(70, 93, 100, 0.16)",
                    },
                  },
                },
                "& .MuiMenuItem-root em": {
                  color: PARTS_MOVEMENTS_COLORS.TEXT_MUTED,
                  fontStyle: "italic",
                },
              },
            },
          }}
          sx={{
            minWidth: "160px",
            backgroundColor: PARTS_MOVEMENTS_COLORS.BACKGROUND_CARD,
            color: PARTS_MOVEMENTS_COLORS.TEXT_CARD,
            fontSize: "0.8rem",
            fontWeight: 500,
            borderRadius: 2,
            transition: "all 0.2s ease",
            "& .MuiOutlinedInput-notchedOutline": {
              border: `1.5px solid ${PARTS_MOVEMENTS_COLORS.BORDER_CARD}`,
            },
            "&:hover": {
              backgroundColor: PARTS_MOVEMENTS_COLORS.BACKGROUND_INPUT,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: PARTS_MOVEMENTS_COLORS.BORDER_BRAND,
                boxShadow: "0 2px 4px rgba(70, 93, 100, 0.1)",
              },
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: PARTS_MOVEMENTS_COLORS.BORDER_BRAND,
              borderWidth: "1.5px",
              boxShadow: "0 0 0 2px rgba(70, 93, 100, 0.1)",
            },
            "& .MuiSelect-select": {
              padding: "8px 12px",
              fontSize: "0.8rem",
            },
          }}>
          <MenuItem value='' sx={{ fontSize: "0.75rem" }}>
            <em>{translateText("Seleccione Usuario", lang)}</em>
          </MenuItem>
          {usuariosFiltrados.map((usuario) => (
            <MenuItem key={usuario.key} value={usuario.name} sx={{ fontSize: "0.75rem" }}>
              {usuario.name}
            </MenuItem>
          ))}
        </Select>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          <Button
            onClick={() => handleAssignAll(usuarioSeleccionado)}
            variant='outlined'
            size="small"
            startIcon={<AssignmentIcon />}
            sx={{
              backgroundColor: "transparent",
              color: PARTS_MOVEMENTS_COLORS.TEXT_BRAND,
              border: `1.5px solid ${PARTS_MOVEMENTS_COLORS.BORDER_BRAND}`,
              borderRadius: 2,
              fontSize: "0.8rem",
              fontWeight: 600,
              padding: "6px 16px",
              textTransform: "none",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: PARTS_MOVEMENTS_COLORS.BORDER_BRAND,
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(70, 93, 100, 0.2)",
              },
            }}>
            {translateText("Asignar todos", lang)}
          </Button>

          <Button
            onClick={toggleMostrarTodosUsuarios}
            variant='outlined'
            size="small"
            startIcon={<PeopleIcon />}
            sx={{
              backgroundColor: mostrarTodosUsuarios ? PARTS_MOVEMENTS_COLORS.BACKGROUND_BUTTON_PRIMARY : "transparent",
              color: mostrarTodosUsuarios ? "white" : PARTS_MOVEMENTS_COLORS.TEXT_BRAND,
              border: `1.5px solid ${PARTS_MOVEMENTS_COLORS.BORDER_BRAND}`,
              borderRadius: 2,
              fontSize: "0.8rem",
              fontWeight: 600,
              padding: "6px 16px",
              textTransform: "none",
              transition: "all 0.2s ease",
              boxShadow: mostrarTodosUsuarios ? "0 2px 8px rgba(70, 93, 100, 0.2)" : "none",
              "&:hover": {
                borderColor: PARTS_MOVEMENTS_COLORS.BORDER_BRAND,
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(70, 93, 100, 0.2)",
              },
            }}>
            {translateText(
              mostrarTodosUsuarios ? "Mostrar solo mi equipo" : "Mostrar todos",
              lang
            )}
          </Button>

          <Button
            variant='outlined'
            size="small"
            startIcon={<PersonAddIcon />}
            onClick={() => setOpenAsignarPorUsuario(true)}
            sx={{
              backgroundColor: "transparent",
              color: PARTS_MOVEMENTS_COLORS.TEXT_BRAND,
              border: `1.5px solid ${PARTS_MOVEMENTS_COLORS.BORDER_BRAND}`,
              borderRadius: 2,
              fontSize: "0.8rem",
              fontWeight: 600,
              padding: "6px 16px",
              textTransform: "none",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: PARTS_MOVEMENTS_COLORS.BORDER_BRAND,
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(70, 93, 100, 0.2)",
              },
            }}>
            {translateText("Asignar por usuario", lang)}
          </Button>
        </Box>

        {searchValue && (
          <Box
            sx={{
              width: "100%",
              mt: 2,
              p: 2,
              backgroundColor: "rgba(59, 130, 246, 0.1)",
              border: "1px solid rgba(59, 130, 246, 0.2)",
              borderRadius: 2,
              backdropFilter: "blur(10px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
            }}>
            <Typography
              variant='body2'
              sx={{
                color: PARTS_MOVEMENTS_COLORS.TEXT_LINK,
                fontWeight: 500,
                fontSize: "0.85rem",
              }}>
              Buscando:{" "}
              <Box component='span' sx={{ fontWeight: 700 }}>
                {searchValue}
              </Box>
            </Typography>
            {searchResult && searchResult.length > 0 && (
              <Chip
                label={`${searchResult.length} MO${searchResult.length > 1 ? 's' : ''} encontrada${searchResult.length > 1 ? 's' : ''}`}
                size="small"
                sx={{
                  backgroundColor: PARTS_MOVEMENTS_COLORS.BACKGROUND_BUTTON_PRIMARY,
                  color: "white",
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
        )}
      </Box>
    );
  };

  const renderItem = (item, index) => {
    const statusEntrega = item.entrega_pieza ? true : false;
    const statusRecibe = item.recibe_pieza ? true : false;

    return (
      <TableRow
        key={item.key}
        sx={{
          "&:hover": {
            backgroundColor: PARTS_MOVEMENTS_COLORS.BACKGROUND_ROW_ALTERNATE,
          },
          borderBottom: `1px solid ${PARTS_MOVEMENTS_COLORS.BORDER_LIGHT}`,
        }}>
        <TableCell
          align='center'
          sx={{
            fontSize: "0.8rem",
            py: 0.75,
            px: 1.5,
            color: PARTS_MOVEMENTS_COLORS.TEXT_PRIMARY,
          }}>
          {index + 1}
        </TableCell>
        <TableCell
          align='center'
          sx={{
            fontSize: "0.8rem",
            py: 0.75,
            px: 1.5,
            color: PARTS_MOVEMENTS_COLORS.TEXT_PRIMARY,
          }}>
          {item.mo}
        </TableCell>
        <TableCell
          align='center'
          sx={{
            fontSize: "0.8rem",
            py: 0.75,
            px: 1.5,
            color: PARTS_MOVEMENTS_COLORS.TEXT_PRIMARY,
          }}>
          {formatNumber(item.cantidad)}
        </TableCell>
        <TableCell
          align='center'
          sx={{
            fontSize: "0.8rem",
            py: 0.75,
            px: 1.5,
            color: PARTS_MOVEMENTS_COLORS.TEXT_PRIMARY,
          }}>
          {item.referencia}
        </TableCell>
        <TableCell
          align='center'
          sx={{
            fontSize: "0.8rem",
            py: 0.75,
            px: 1.5,
            color: PARTS_MOVEMENTS_COLORS.TEXT_PRIMARY,
          }}>
          {formatDate(item.fechaentrega)}
        </TableCell>
        <TableCell
          align='center'
          sx={{
            fontSize: "0.8rem",
            py: 0.75,
            px: 1.5,
            color: PARTS_MOVEMENTS_COLORS.TEXT_PRIMARY,
            maxWidth: 200,
          }}>
          <Typography
            sx={{
              fontSize: "0.75rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}>
            {item.observaciones ? item.observaciones : "..."}
          </Typography>
        </TableCell>
        <TableCell
          align='center'
          sx={{
            fontSize: "0.9rem",
            py: 0.75,
          }}>
          <Box sx={{ 
            display: "flex", 
            flexDirection: "row", 
            gap: 0.75, 
            alignItems: "center", 
            justifyContent: "center", 
            flexWrap: "nowrap",
            width: "100%",
            maxWidth: "450px",
            margin: "0 auto",
          }}>
            <Box sx={{ flex: 1, display: "flex", justifyContent: "center", minWidth: 0 }}>
              {item.entrega_pieza ? (
                <Tooltip title={`Ent: ${item.entrega_pieza}`} arrow>
                  <Chip
                    label={`Ent: ${item.entrega_pieza}`}
                    size="small"
                    sx={{
                      backgroundColor: "rgba(23, 64, 69, 0.15)",
                      color: BRAND_COLORS.ACCENT,
                      fontWeight: 500,
                      fontSize: "0.7rem",
                      height: "20px",
                      border: `1px solid rgba(23, 64, 69, 0.3)`,
                      cursor: "help",
                      transition: "all 0.2s ease",
                      maxWidth: "100%",
                      "&:hover": {
                        backgroundColor: "rgba(23, 64, 69, 0.25)",
                        borderColor: "rgba(23, 64, 69, 0.4)",
                      },
                      "& .MuiChip-label": {
                        padding: "0 6px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  />
                </Tooltip>
              ) : (
                <Chip
                  label="No Entregado"
                  size="small"
                  sx={{
                    backgroundColor: "rgba(148, 163, 184, 0.12)",
                    color: PARTS_MOVEMENTS_COLORS.TEXT_SECONDARY,
                    fontWeight: 400,
                    fontSize: "0.75rem",
                    height: "22px",
                    border: `1px solid rgba(148, 163, 184, 0.25)`,
                    maxWidth: "100%",
                    "& .MuiChip-label": {
                      padding: "0 8px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
              )}
            </Box>
            <Box sx={{ flex: 1, display: "flex", justifyContent: "center", minWidth: 0 }}>
              {item.recibe_pieza ? (
                <Tooltip title={`Rec: ${item.recibe_pieza}`} arrow>
                  <Chip
                    label={`Rec: ${item.recibe_pieza}`}
                    size="small"
                    sx={{
                      backgroundColor: "rgba(49, 79, 91, 0.15)",
                      color: BRAND_COLORS.PRIMARY,
                      fontWeight: 500,
                      fontSize: "0.7rem",
                      height: "20px",
                      border: `1px solid rgba(49, 79, 91, 0.3)`,
                      cursor: "help",
                      transition: "all 0.2s ease",
                      maxWidth: "100%",
                      "&:hover": {
                        backgroundColor: "rgba(49, 79, 91, 0.25)",
                        borderColor: "rgba(49, 79, 91, 0.4)",
                      },
                      "& .MuiChip-label": {
                        padding: "0 6px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  />
                </Tooltip>
              ) : (
                <Chip
                  label="No Recibido"
                  size="small"
                  sx={{
                    backgroundColor: "rgba(148, 163, 184, 0.12)",
                    color: PARTS_MOVEMENTS_COLORS.TEXT_SECONDARY,
                    fontWeight: 400,
                    fontSize: "0.75rem",
                    height: "22px",
                    border: `1px solid rgba(148, 163, 184, 0.25)`,
                    maxWidth: "100%",
                    "& .MuiChip-label": {
                      padding: "0 8px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
              )}
            </Box>
          </Box>
        </TableCell>
        <TableCell align='center' sx={{ py: 0.75, px: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              justifyContent: "center",
              width: "240px",
              margin: "0 auto",
            }}>
            {!statusRecibe ? (
              <>
                <Button
                  onClick={() => handleAddEntrega(item)}
                  variant='contained'
                  disabled={statusEntrega && !statusRecibe}
                  size='small'
                  sx={{
                    backgroundColor: BRAND_COLORS.PRIMARY,
                    color: "white",
                    textTransform: "none",
                    borderRadius: 1,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    flex: 1,
                    maxWidth: "110px",
                    boxShadow: "none",
                    py: 0.5,
                    "&:hover": {
                      backgroundColor: BRAND_COLORS.PRIMARY_DARK,
                      boxShadow: "0 2px 4px rgba(49, 79, 91, 0.3)",
                    },
                    "&:disabled": {
                      backgroundColor: BORDER_COLORS.SECONDARY,
                      color: TEXT_COLORS.TERTIARY,
                    },
                  }}>
                  {translateText("Entrega", lang)}
                </Button>
                <Button
                  onClick={() => handleAddRecibido(item)}
                  variant='contained'
                  disabled={statusRecibe && statusEntrega}
                  size='small'
                  sx={{
                    backgroundColor: BRAND_COLORS.PRIMARY,
                    color: "white",
                    textTransform: "none",
                    borderRadius: 1,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    flex: 1,
                    maxWidth: "110px",
                    boxShadow: "none",
                    py: 0.5,
                    "&:hover": {
                      backgroundColor: BRAND_COLORS.PRIMARY_DARK,
                      boxShadow: "0 2px 4px rgba(49, 79, 91, 0.3)",
                    },
                    "&:disabled": {
                      backgroundColor: BORDER_COLORS.SECONDARY,
                      color: TEXT_COLORS.TERTIARY,
                    },
                  }}>
                  {translateText("Taller", lang)}
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => handleAddEntRec(item)}
                  variant='contained'
                  size='small'
                  sx={{
                    backgroundColor: BRAND_COLORS.PRIMARY,
                    color: "white",
                    textTransform: "none",
                    borderRadius: 1,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    flex: 1,
                    maxWidth: "110px",
                    boxShadow: "none",
                    py: 0.5,
                    "&:hover": {
                      backgroundColor: BRAND_COLORS.PRIMARY_DARK,
                      boxShadow: "0 2px 4px rgba(49, 79, 91, 0.3)",
                    },
                  }}>
                  {translateText("Asignar a", lang)}
                </Button>
                <Select
                  value={usuarioSeleccionados[item.key] || ""}
                  onChange={(e) => {
                    seleccionarUsuario(item.key, e.target.value);
                  }}
                  displayEmpty
                  size='small'
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: 2,
                        border: `1.5px solid ${BRAND_COLORS.SECONDARY}`,
                        boxShadow: "0 4px 12px rgba(70, 93, 100, 0.2)",
                        mt: 1,
                        maxHeight: 400,
                        "& .MuiMenuItem-root": {
                          fontSize: "0.75rem",
                          padding: "6px 12px",
                          color: TEXT_COLORS.PRIMARY,
                          "&:hover": {
                            backgroundColor: "rgba(70, 93, 100, 0.1)",
                            color: BRAND_COLORS.SECONDARY,
                          },
                          "&.Mui-selected": {
                            backgroundColor: "rgba(70, 93, 100, 0.15)",
                            color: BRAND_COLORS.SECONDARY,
                            fontWeight: 500,
                            "&:hover": {
                              backgroundColor: "rgba(70, 93, 100, 0.2)",
                            },
                          },
                        },
                        "& .MuiMenuItem-root em": {
                          color: PARTS_MOVEMENTS_COLORS.TEXT_MUTED,
                          fontStyle: "italic",
                        },
                      },
                    },
                  }}
                  sx={{
                    fontSize: "0.75rem",
                    flex: 1,
                    maxWidth: "110px",
                    backgroundColor: PARTS_MOVEMENTS_COLORS.BACKGROUND_ROW_ALTERNATE,
                    borderRadius: 1,
                    height: "28px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: PARTS_MOVEMENTS_COLORS.BORDER_NEUTRAL,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: BRAND_COLORS.PRIMARY,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: BRAND_COLORS.PRIMARY,
                      borderWidth: "1px",
                    },
                    "& .MuiSelect-select": {
                      py: 0.5,
                      fontSize: "0.75rem",
                    },
                  }}>
                  <MenuItem value='' sx={{ fontSize: "0.75rem" }}>
                    <em>...</em>
                  </MenuItem>
                  {usuariosFiltrados.map((usuario) => (
                    <MenuItem key={usuario.key} value={usuario.name} sx={{ fontSize: "0.75rem" }}>
                      {usuario.name}
                    </MenuItem>
                  ))}
                </Select>
              </>
            )}
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  const renderList = () => {
    if (!isLoading && listData.length === 0) {
      return (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            color: "text.secondary",
            backgroundColor: "white",
            borderRadius: 2,
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${PARTS_MOVEMENTS_COLORS.BORDER_LIGHT}`,
          }}>
          <Typography variant='body1' sx={{ fontSize: "0.95rem" }}>
            {translateText("No hay datos disponibles.", lang)}
          </Typography>
        </Box>
      );
    }
    const dataToRender =
      searchResult && searchResult.length > 0 ? searchResult : listData;

    const paginatedData = dataToRender.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    return (
      <Paper
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          border: `1px solid ${PARTS_MOVEMENTS_COLORS.BORDER_LIGHT}`,
        }}>
        {isLoading ? (
          <Box sx={{ p: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      "#",
                      translateText("MO", lang),
                      translateText("Cantidad", lang),
                      translateText("Referencia", lang),
                      translateText("Entrega", lang),
                      translateText("Observaciones", lang),
                      translateText("Estado", lang),
                      translateText("Acciones", lang),
                    ].map((header, i) => (
                      <TableCell key={i} sx={{ py: 1, px: 1.5 }}>
                        <Skeleton variant="text" width="100%" height={24} />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(8)].map((_, j) => (
                        <TableCell key={j} sx={{ py: 0.75, px: 1.5 }}>
                          <Skeleton variant="text" width="100%" height={20} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: "calc(78vh - 60px)" }}>
            <Table stickyHeader size="small" sx={{ width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: PARTS_MOVEMENTS_COLORS.BACKGROUND_TABLE_HEADER,
                      borderBottom: `1px solid ${PARTS_MOVEMENTS_COLORS.BORDER_TABLE}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    #
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: PARTS_MOVEMENTS_COLORS.BACKGROUND_TABLE_HEADER,
                      borderBottom: `1px solid ${PARTS_MOVEMENTS_COLORS.BORDER_TABLE}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("MO", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: PARTS_MOVEMENTS_COLORS.BACKGROUND_TABLE_HEADER,
                      borderBottom: `1px solid ${PARTS_MOVEMENTS_COLORS.BORDER_TABLE}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Cantidad", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: PARTS_MOVEMENTS_COLORS.BACKGROUND_TABLE_HEADER,
                      borderBottom: `1px solid ${PARTS_MOVEMENTS_COLORS.BORDER_TABLE}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Referencia", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: PARTS_MOVEMENTS_COLORS.BACKGROUND_TABLE_HEADER,
                      borderBottom: `1px solid ${PARTS_MOVEMENTS_COLORS.BORDER_TABLE}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Entrega", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: PARTS_MOVEMENTS_COLORS.BACKGROUND_TABLE_HEADER,
                      borderBottom: `1px solid ${PARTS_MOVEMENTS_COLORS.BORDER_TABLE}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Observaciones", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: PARTS_MOVEMENTS_COLORS.BACKGROUND_TABLE_HEADER,
                      borderBottom: `1px solid ${PARTS_MOVEMENTS_COLORS.BORDER_TABLE}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Estado", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: PARTS_MOVEMENTS_COLORS.BACKGROUND_TABLE_HEADER,
                      borderBottom: `1px solid ${PARTS_MOVEMENTS_COLORS.BORDER_TABLE}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Acciones", lang)}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((item, index) => renderItem(item, index))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!isLoading && (
          <>
            <Divider />

            <Box sx={{ backgroundColor: PARTS_MOVEMENTS_COLORS.BACKGROUND_WHITE }}>
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component='div'
                count={dataToRender.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={translateText("Filas por página", lang)}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} ${translateText("de", lang)} ${count}`
                }
                sx={{
                  "& .MuiTablePagination-toolbar": {
                    fontSize: "0.75rem",
                    color: PARTS_MOVEMENTS_COLORS.TEXT_SECONDARY,
                    minHeight: "52px",
                  },
                  "& .MuiTablePagination-select": {
                    fontSize: "0.75rem",
                  },
                  "& .MuiTablePagination-displayedRows": {
                    fontSize: "0.75rem",
                  },
                }}
              />
            </Box>
          </>
        )}
      </Paper>
    );
  };

  return (
    <Box sx={{ padding: 1.5 }}>
      <Box>
        <SearchBar
          handleSearch={handleSearch}
          toggleMostrarTodosUsuarios={toggleMostrarTodosUsuarios}
          mostrarTodosUsuarios={mostrarTodosUsuarios}
          inputRef={inputRef}
          usuariosFiltrados={usuariosFiltrados}
          handleAssignAll={handleAssignAll}
          searchResult={searchResult}
        />
      </Box>
      {renderList()}
      <AsignarPorUsuarioModal
        open={openAsignarPorUsuario}
        onClose={() => setOpenAsignarPorUsuario(false)}
        usuariosTaller={usuariosTaller}
        listData={listData}
        lang={lang}
        onAsignarSeleccionadas={async (usuarioAsignado, keys, team) => {
          for (const item of listData) {
            if (keys.includes(item.key)) {
              await handleAddEntRec(item, usuarioAsignado);
            }
          }
        }}
      />

      {/* Modal dinámico para usuarios externos */}
      {externoModal && isExternoModalOpen && (
        <externoModal.component
          open={isExternoModalOpen}
          onClose={() => {
            setIsExternoModalOpen(false);
            setExternoModal(null);
          }}
          {...externoModal.props}
        />
      )}
    </Box>
  );
}
